import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { debounce, isEmpty } from 'lodash';

import { ApplicationContext } from '../../../ApplicationContext';

import {
  setFirstNameFieldValueAction,
  setIdentificationPersonTypeFieldValueAction,
  setInnFieldValueAction,
  setLastNameFieldValueAction,
  setSecondNameFieldValueAction,
  setCadastralNumberFieldValueAction,
  setCompanyName,
  validateAllAction,
  validateByFieldAction,
  resetState,
} from './internal/actions';
import { CommercialOwnershipVerificationFormView } from './internal/components/CommercialOwnershipVerificationFormView';
import { validationRulesEntries, areAllFieldsRequiredFilled } from './internal/utils';
import { getPlacementApplicationFormInitialState, placementApplicationFormReducer } from './internal/reducer';
import { fieldsByType } from './internal/maps';
import { useFetchCustomerNameByInn } from './internal/hooks';
import {
  CheckFormFieldInvalid,
  GetFormFieldErrorMessage,
  GetFormFieldValue,
  HandleBlur,
  HandleSubmit,
  ICoworkingCommercialOwnershipVerificationFormViewProps,
  GetFormFieldRequire,
  GetFormFieldVisible,
  HandleCancel,
  THandleInnChange,
  TDebouncedFetch,
} from './types';
import { ECommercialOwnershipVerificationFormViewFields, EIdentificationPersonType } from './enums';
import { IOfferHomeowner } from '../../../../redux/reducers';

export const CommercialOwnershipVerificationForm: React.FC<
  ICoworkingCommercialOwnershipVerificationFormViewProps
> = props => {
  const { isDisableFieldsOnPending = false, formInitialState, onSubmit, onCancel, isMobile = false, onChange } = props;

  const [formState, formDispatch] = React.useReducer(
    placementApplicationFormReducer,
    getPlacementApplicationFormInitialState(formInitialState),
  );

  const boundActions = React.useMemo(
    () =>
      bindActionCreators(
        {
          setFirstNameFieldValueAction,
          setIdentificationPersonTypeFieldValueAction,
          setInnFieldValueAction,
          setLastNameFieldValueAction,
          setSecondNameFieldValueAction,
          setCadastralNumberFieldValueAction,
          setCompanyName,
          validateAllAction,
          validateByFieldAction,
          resetState,
        },
        formDispatch as Dispatch,
      ),
    [],
  );

  const formValuesValid = React.useMemo(
    () => !validationRulesEntries.some(([ruleKey, ruleFunc]) => ruleFunc(formState[ruleKey].value, formState)),
    [formState],
  );

  const handleSubmit = React.useCallback<HandleSubmit>(
    event => {
      event.preventDefault();

      if (formValuesValid) {
        onSubmit(formState);
      } else {
        boundActions.validateAllAction();
      }
    },
    [formState, onSubmit, boundActions],
  );

  const handleCancel = React.useCallback<HandleCancel>(() => {
    boundActions.resetState(formInitialState);

    onCancel();
  }, [onCancel, boundActions, formInitialState]);

  const handleBlur: HandleBlur = fieldName => () => boundActions.validateByFieldAction(fieldName);

  const getFormFieldErrorMessage = React.useCallback<GetFormFieldErrorMessage>(
    fieldType => formState[fieldType].errorMessage,
    [formState],
  );

  const checkFormFieldInvalid = React.useCallback<CheckFormFieldInvalid>(
    type => !!getFormFieldErrorMessage(type),
    [getFormFieldErrorMessage],
  );

  const getFormFieldValue: GetFormFieldValue = fieldType => formState[fieldType].value;

  const getConfig = () => {
    const identificationPersonType = getFormFieldValue(
      ECommercialOwnershipVerificationFormViewFields.IdentificationPersonType,
    );

    return identificationPersonType
      ? fieldsByType[identificationPersonType as EIdentificationPersonType]
      : fieldsByType.none;
  };

  const getFormFieldRequired: GetFormFieldRequire = React.useCallback(
    fieldType => {
      const config = getConfig();

      const fieldValue = getFormFieldValue(fieldType);

      return !fieldValue && config.require.includes(fieldType);
    },
    [formState],
  );

  const getFormFieldVisible: GetFormFieldVisible = React.useCallback(
    fieldType => {
      const config = getConfig();

      return config.visible.includes(fieldType);
    },
    [formState.identificationPersonType.value],
  );

  const { logger, httpApi } = React.useContext(ApplicationContext);
  const { callRequest: callGetCustomerNameByInn } = useFetchCustomerNameByInn(httpApi, logger);

  const debouncedCallGetCustomerNameByInn = debounce<TDebouncedFetch>(inn => {
    callGetCustomerNameByInn(
      { inn },
      {
        onSuccess: ({ name }) => boundActions.setCompanyName(name),
      },
    );
  }, 300);

  const handleOnInnChange = React.useCallback<THandleInnChange>(
    (_, inn) => {
      boundActions.setInnFieldValueAction(inn);

      if (formState.identificationPersonType.value !== EIdentificationPersonType.Legal) {
        return;
      }

      debouncedCallGetCustomerNameByInn(inn);
    },
    [formState.identificationPersonType.value],
  );

  React.useEffect(() => {
    if (!areAllFieldsRequiredFilled(formState)) {
      onChange(undefined);

      // tslint:disable-next-line: return-undefined
      return;
    }

    const homeOwner = Object.entries(formState).reduce((acc, [name, fieldData]) => {
      const isFieldInvalid = checkFormFieldInvalid(name as ECommercialOwnershipVerificationFormViewFields);

      if (!fieldData.value || isFieldInvalid) {
        return acc;
      }

      return {
        ...acc,
        [name]: fieldData.value,
      };
    }, {} as IOfferHomeowner);

    if (!isEmpty(homeOwner)) {
      onChange(homeOwner);
    }
  }, [formState]);

  return (
    <CommercialOwnershipVerificationFormView
      boundActions={boundActions}
      checkFormFieldInvalid={checkFormFieldInvalid}
      isDisableFieldsOnPending={isDisableFieldsOnPending}
      getFormFieldErrorMessage={getFormFieldErrorMessage}
      getFormFieldValue={getFormFieldValue}
      getFormFieldRequired={getFormFieldRequired}
      getFormFieldVisible={getFormFieldVisible}
      onBlur={handleBlur}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      pending={!formValuesValid}
      isMobile={isMobile}
      onInnChange={handleOnInnChange}
    />
  );
};

CommercialOwnershipVerificationForm.displayName = 'CommercialOwnershipVerificationForm';
